import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiPlus, FiPrinter, FiTrash2, FiEdit2,
  FiCheck, FiX, FiPercent, FiSearch, FiList,
} from 'react-icons/fi';
import * as kgService from '../services/kassaGoldService';

/* Pure helpers (mirror GoldPurchaseDashboard) */
function money(n) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
}
function grams3(n) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
}
function editVal(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) && n !== 0 ? String(v) : '';
}
// Format a unix-seconds timestamp as HH:MM (local time).
function timeHM(unixSeconds) {
  const n = parseInt(unixSeconds, 10);
  if (!Number.isFinite(n) || n <= 0) return '';
  return new Date(n * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
function sumGold(rows) {
  return rows.reduce((acc, r) => acc + (parseFloat(r.grams) || 0), 0);
}
function replayGold(opening, rows, adjustments) {
  const events = [];
  for (const r of rows) events.push({ ts: parseInt(r.created_at) || 0, id: parseInt(r.id) || 0, kind: 'buy', amount: parseFloat(r.grams) || 0 });
  for (const a of adjustments) events.push({ ts: parseInt(a.created_at) || 0, id: parseInt(a.id) || 0, kind: a.type, amount: parseFloat(a.amount) || 0 });
  events.sort((x, y) => (x.ts - y.ts) || (x.id - y.id));
  let g = opening;
  for (const e of events) {
    if (e.kind === 'buy') g += e.amount;
    else if (e.kind === 'set') g = e.amount;
    else if (e.kind === 'subtract') g -= e.amount;
  }
  return g;
}

/**
 * Kassa Gold buy-log ledger. A duplicate of GoldPurchaseDashboard's ledger body,
 * embedded inside the Kassa module. The date is controlled by the parent; after
 * any mutation it calls onChanged() so the shared Kassa saldo refreshes, and
 * printing is delegated to the parent via onPrint(row).
 *
 * @param {object} props
 * @param {string} props.date   YYYY-MM-DD (controlled by parent)
 * @param {function} props.t
 * @param {function} props.onChanged  called after add/edit/delete so saldo updates
 * @param {function} props.onPrint    called with a row to print its receipt
 */
export default function KassaGoldLedger({ date, t, locked = false, onChanged, onPrint, onDate }) {
  const [rows, setRows] = useState([]);
  const [defaultRate, setDefaultRateState] = useState(37);
  const [rateInput, setRateInput] = useState('37');
  const [goldOpening, setGoldOpening] = useState(0);
  const [goldAdjustments, setGoldAdjustments] = useState([]);
  const [showGoldModal, setShowGoldModal] = useState(false);
  const [showSubs, setShowSubs] = useState(false);
  const [showTeor, setShowTeor] = useState(false);
  const [searchNote, setSearchNote] = useState('');
  const [searchError, setSearchError] = useState('');
  const [highlightId, setHighlightId] = useState(null);

  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const lastVersion = useRef('');
  const pendingSync = useRef(false);
  const dateRef = useRef(date); dateRef.current = date;
  const draftRef = useRef(draft); draftRef.current = draft;

  const loadDay = async (iso) => {
    const res = await kgService.getDay(iso);
    if (res.success && res.data) {
      setRows(res.data.rows || []);
      setGoldAdjustments(res.data.adjustments || []);
      setGoldOpening(parseFloat(res.data.gold_opening) || 0);
      const rate = parseFloat(res.data.default_rate) || 37;
      setDefaultRateState(rate);
      setRateInput(String(rate));
      const v = await kgService.getDayVersion(iso);
      if (v.success && v.data) lastVersion.current = v.data.version;
    }
  };

  useEffect(() => {
    loadDay(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Live sync: poll every 4s; defer reload while a draft is open.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      const iso = dateRef.current;
      const v = await kgService.getDayVersion(iso);
      if (cancelled || !v.success || !v.data) return;
      if (v.data.version === lastVersion.current) return;
      if (draftRef.current) pendingSync.current = true;
      else await loadDay(iso);
    };
    const id = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!draft && pendingSync.current) {
      pendingSync.current = false;
      loadDay(dateRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  // Payment difference per row (in SRD): owed - paid. Positive = underpaid,
  // negative = overpaid. paid converted to SRD via the row's rate.
  const rowDiff = (r) => (parseFloat(r.total_srd) || 0)
    - (parseFloat(r.paid_usd) || 0) * (parseFloat(r.rate_srd) || 0)
    - (parseFloat(r.paid_srd) || 0);
  const totalDiff = useMemo(() => rows.reduce((s, r) => s + rowDiff(r), 0), [rows]);
  const subtractions = useMemo(() => goldAdjustments.filter((a) => a.type === 'subtract'), [goldAdjustments]);
  const totalSubtracted = useMemo(() => subtractions.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0), [subtractions]);
  const goldBought = useMemo(() => sumGold(rows), [rows]);
  const goldClosing = useMemo(() => replayGold(goldOpening, rows, goldAdjustments), [goldOpening, rows, goldAdjustments]);
  const dayTotals = useMemo(() => rows.reduce((acc, r) => ({
    usd: acc.usd + (parseFloat(r.total_usd) || 0),
    srd: acc.srd + (parseFloat(r.total_srd) || 0),
  }), { usd: 0, srd: 0 }), [rows]);
  const paidTotals = useMemo(() => rows.reduce((acc, r) => ({
    usd: acc.usd + (parseFloat(r.paid_usd) || 0),
    srd: acc.srd + (parseFloat(r.paid_srd) || 0),
    eur: acc.eur + (parseFloat(r.paid_eur) || 0),
  }), { usd: 0, srd: 0, eur: 0 }), [rows]);

  const reload = async () => { await loadDay(date); if (onChanged) onChanged(); };

  /* Gold adjustments */
  const applyGoldAction = async (type, amount) => {
    setShowGoldModal(false);
    const res = await kgService.addAdjustment(date, type, parseFloat(amount) || 0);
    if (res.success) await reload();
    else alert(res.message || 'Failed to adjust gold');
  };
  const undoLastGoldAdjustment = async () => {
    const res = await kgService.undoAdjustment(date);
    if (res.success) await reload();
  };

  /* Row editing */
  const startAdd = () => setDraft({ id: null, grams: '', price_per_gram: '', rate_srd: String(defaultRate), paid_usd: '', paid_srd: '', paid_eur: '' });
  const startEdit = (row) => setDraft({
    id: row.id, grams: row.grams, price_per_gram: row.price_per_gram, rate_srd: row.rate_srd,
    paid_usd: row.paid_usd, paid_srd: row.paid_srd, paid_eur: row.paid_eur,
  });
  const setDraftField = (key, val) => setDraft((d) => ({ ...d, [key]: val }));
  const cancelDraft = () => setDraft(null);

  const confirmDraft = async () => {
    if (!draft || saving) return;
    setSaving(true);
    try {
      let id = draft.id;
      if (!id) {
        const created = await kgService.addRow(date);
        if (!created.success || !created.data) { alert(created.message || 'Failed to add row'); return; }
        id = created.data.id;
      }
      const res = await kgService.updateRow(id, {
        grams: draft.grams, price_per_gram: draft.price_per_gram, rate_srd: draft.rate_srd,
        paid_usd: draft.paid_usd, paid_srd: draft.paid_srd, paid_eur: draft.paid_eur,
      });
      if (res.success) { setDraft(null); await reload(); }
      else alert(res.message || 'Failed to save row');
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm(t('gp_delete_confirm'))) return;
    const res = await kgService.deleteRow(id);
    if (res.success) await reload();
    else alert(res.message || 'Failed to delete row');
  };

  /* Rate */
  const commitDefaultRate = async (val) => {
    const r = parseFloat(val);
    if (!Number.isFinite(r) || r <= 0) { setRateInput(String(defaultRate)); return; }
    setDefaultRateState(r);
    setRateInput(String(r));
    await kgService.setDefaultRate(r);
  };

  /* Search by note */
  const runSearch = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const q = searchNote.trim();
    if (!q) return;
    setSearchError('');
    const res = await kgService.findByNote(q);
    if (res.success && res.data) {
      setHighlightId(res.data.id);
      setTimeout(() => setHighlightId(null), 4000);
      // Jump to the row's day (date is controlled by the parent).
      if (res.data.purchase_date && res.data.purchase_date !== date && onDate) {
        onDate(res.data.purchase_date);
      }
    } else {
      setSearchError(res.message || 'Not found');
      setTimeout(() => setSearchError(''), 3000);
    }
  };

  return (
    <>
      {/* Controls: rate + teor + note search */}
      <div className="gp-datebar">
        <div className="gp-rate-control">
          <span className="muted small">{t('gp_default_rate')}</span>
          <div className="input-group gp-rate-input">
            <span className="prefix" aria-hidden>1$=</span>
            <input
              type="number" step="0.001" inputMode="decimal"
              value={rateInput}
              disabled={locked}
              onChange={(e) => setRateInput(e.target.value)}
              onBlur={(e) => commitDefaultRate(e.target.value)}
              title={t('gp_exchange_rate')}
            />
            <span className="prefix" aria-hidden>SRD</span>
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowTeor(true)} title={t('gp_teor_calc')}>
          <FiPercent style={{ marginRight: '6px' }} /> {t('gp_teor_calc')}
        </button>
        <form className="gp-search" onSubmit={runSearch}>
          <div className="input-group gp-search-input">
            <span className="prefix" aria-hidden><FiSearch /></span>
            <input
              type="text" value={searchNote}
              onChange={(e) => setSearchNote(e.target.value)}
              placeholder={t('gp_search_note')} aria-label={t('gp_search_note')}
            />
          </div>
          {searchError && <span className="gp-search-error">{searchError}</span>}
        </form>
      </div>

      {/* Gold grams summary */}
      <div className="gp-balance-bar">
        <div className="gp-bal-group">
          <span className="gp-bal-label">
            {t('gp_gold_opening')}
            {goldOpening > 0 && <span className="gp-carry" title="Carried from previous day"> ↳</span>}
          </span>
          <span className="gp-gold-val">{grams3(goldOpening)} g</span>
        </div>
        <span className="gp-bal-sep gp-bal-sep-plus">+</span>
        <div className="gp-bal-group">
          <span className="gp-bal-label">{t('gp_gold_bought')}</span>
          <span className="gp-gold-val gp-gold-pos">{grams3(goldBought)} g</span>
        </div>
        {goldAdjustments.length > 0 && (
          <div className="gp-bal-group">
            <span className="gp-bal-label">{t('gp_gold_adjustments')}</span>
            <span className="gp-gold-val gp-gold-neg">{grams3(goldClosing - goldOpening - goldBought)} g</span>
            <button className="btn btn-ghost btn-sm" onClick={undoLastGoldAdjustment} title={t('gp_undo')} disabled={locked}>{t('gp_undo')}</button>
          </div>
        )}
        <span className="gp-bal-sep">=</span>
        <div className="gp-bal-group gp-bal-group-gold">
          <span className="gp-bal-label">{t('gp_gold_closing')}</span>
          <span className="gp-gold-val gp-gold-strong">{grams3(goldClosing)} g</span>
          <button className="btn btn-ghost btn-sm gp-gold-action" onClick={() => setShowGoldModal(true)} disabled={locked}>{t('gp_adjust_gold')}</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowSubs(true)} title={t('gp_subtractions')}>
            <FiList style={{ marginRight: '6px' }} /> {t('gp_subtractions')}
          </button>
        </div>
      </div>

      {/* Purchases table */}
      <div className="gp-table-wrap">
        <table className="gp-table">
          <thead>
            <tr>
              <th>{t('gp_note')}</th>
              <th style={{ width: '70px' }}>{t('kassa_time')}</th>
              <th className="gp-num">{t('gp_grams')}</th>
              <th className="gp-num">{t('gp_price')}</th>
              <th className="gp-num">{t('gp_total_usd')}</th>
              <th className="gp-num">{t('gp_total_srd')}</th>
              <th className="gp-num">{t('gp_rate')}</th>
              <th className="gp-num gp-paid-col">{t('gp_paid_usd')}</th>
              <th className="gp-num gp-paid-col">{t('gp_paid_srd')}</th>
              <th className="gp-num gp-paid-col">{t('gp_paid_eur')}</th>
              <th className="gp-num">{t('gp_difference')}</th>
              <th className="gp-actions-col"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !draft && (
              <tr><td colSpan={12} className="gp-empty muted">{t('gp_no_rows')}</td></tr>
            )}
            {rows.map((r) => {
              if (draft && draft.id === r.id) {
                return (
                  <DraftRow key={r.id} note={r.note_number} draft={draft} defaultRate={defaultRate} saving={saving}
                    onField={setDraftField} onConfirm={confirmDraft} onCancel={cancelDraft} t={t} />
                );
              }
              return (
                <tr key={r.id} className={highlightId === r.id ? 'gp-row-highlight' : undefined}>
                  <td className="gp-note">{r.note_number}</td>
                  <td className="gp-ro kassa-time">{timeHM(r.created_at)}</td>
                  <td className="gp-num gp-ro">{money(r.grams)}</td>
                  <td className="gp-num gp-ro">{money(r.price_per_gram)}</td>
                  <td className="gp-num gp-computed">$ {money(r.total_usd)}</td>
                  <td className="gp-num gp-computed">SRD {money(r.total_srd)}</td>
                  <td className="gp-num gp-ro">{money(r.rate_srd)}</td>
                  <td className="gp-num gp-paid-col gp-ro">{money(r.paid_usd)}</td>
                  <td className="gp-num gp-paid-col gp-ro">{money(r.paid_srd)}</td>
                  <td className="gp-num gp-paid-col gp-ro">{money(r.paid_eur)}</td>
                  {(() => {
                    const d = rowDiff(r);
                    const zero = Math.abs(d) < 0.005;
                    const cls = zero ? 'gp-ro' : (d > 0 ? 'gp-gold-pos' : 'gp-gold-neg');
                    return <td className={'gp-num ' + cls}>{zero ? '—' : `${d > 0 ? '+' : '−'} SRD ${money(Math.abs(d))}`}</td>;
                  })()}
                  <td className="gp-actions-col">
                    <div className="gp-row-actions">
                      <button className="btn btn-ghost btn-icon" title={t('gp_edit')} onClick={() => startEdit(r)} disabled={!!draft || locked}><FiEdit2 /></button>
                      <button className="btn btn-ghost btn-icon" title={t('gp_print')} onClick={() => onPrint && onPrint(r)} disabled={!!draft}><FiPrinter /></button>
                      <button className="btn btn-ghost btn-icon" title={t('gp_delete')} onClick={() => deleteRow(r.id)} disabled={!!draft || locked}><FiTrash2 /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {draft && draft.id === null && (
              <DraftRow note={t('gp_new_row')} draft={draft} defaultRate={defaultRate} saving={saving}
                onField={setDraftField} onConfirm={confirmDraft} onCancel={cancelDraft} t={t} />
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td className="gp-foot-label">{t('gp_day_total')}</td>
                <td></td>
                <td className="gp-num gp-foot-total">{grams3(goldBought)} g</td>
                <td></td>
                <td className="gp-num gp-foot-total">$ {money(dayTotals.usd)}</td>
                <td className="gp-num gp-foot-total">SRD {money(dayTotals.srd)}</td>
                <td></td>
                <td className="gp-num gp-foot-total">$ {money(paidTotals.usd)}</td>
                <td className="gp-num gp-foot-total">SRD {money(paidTotals.srd)}</td>
                <td className="gp-num gp-foot-total">€ {money(paidTotals.eur)}</td>
                <td className={'gp-num gp-foot-total ' + (Math.abs(totalDiff) < 0.005 ? '' : (totalDiff > 0 ? 'gp-gold-pos' : 'gp-gold-neg'))}>
                  {Math.abs(totalDiff) < 0.005 ? '—' : `${totalDiff > 0 ? '+' : '−'} SRD ${money(Math.abs(totalDiff))}`}
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>

        {locked ? (
          <div className="kassa-locked-note muted">{t('kassa_locked_note')}</div>
        ) : (
          <button className="btn btn-primary gp-add-btn" onClick={startAdd} disabled={!!draft}>
            <FiPlus style={{ marginRight: '6px' }} /> {t('gp_add_row')}
          </button>
        )}
      </div>

      {showGoldModal && <GoldAdjustModal current={goldClosing} onCancel={() => setShowGoldModal(false)} onApply={applyGoldAction} t={t} />}
      {showTeor && <TeorCalculator onClose={() => setShowTeor(false)} t={t} />}
      {showSubs && (
        <GoldSubtractionsModal date={date} subs={subtractions} total={totalSubtracted} onClose={() => setShowSubs(false)} t={t} />
      )}
    </>
  );
}

// Breakdown of the day's gold subtraction adjustments (time + grams + total).
function GoldSubtractionsModal({ date, subs, total, onClose, t }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" style={{ maxWidth: '460px' }}>
        <div className="modal-header">
          <h3>{t('gp_subtractions')}</h3>
          <div className="muted small">{date}</div>
        </div>
        <div className="modal-body">
          {subs.length === 0 ? (
            <div className="muted" style={{ fontStyle: 'italic' }}>{t('gp_subtractions_none')}</div>
          ) : (
            <table className="gp-table" style={{ minWidth: 0 }}>
              <thead>
                <tr>
                  <th>{t('kassa_time')}</th>
                  <th className="gp-num">{t('gp_grams')}</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((a) => (
                  <tr key={a.id}>
                    <td className="gp-ro kassa-time">{timeHM(a.created_at)}</td>
                    <td className="gp-num gp-gold-neg">− {grams3(a.amount)} g</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="gp-foot-label">{t('total')}</td>
                  <td className="gp-num gp-foot-total gp-gold-neg">− {grams3(total)} g</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>{t('close')}</button>
        </div>
      </div>
    </div>
  );
}

/* ---- Sub-components (copied from GoldPurchaseDashboard) ---- */

const TEOR_A = 2307.454;
const TEOR_B = 2088.136;

function TeorCalculator({ onClose, t }) {
  const [water, setWater] = useState('');
  const [werkelijk, setWerkelijk] = useState('');
  const w = parseFloat(water);
  const r = parseFloat(werkelijk);
  const valid = Number.isFinite(w) && Number.isFinite(r) && r !== 0;
  const teor = valid ? (w / r) * TEOR_A - TEOR_B : null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-header"><h3>{t('gp_teor_calc')}</h3></div>
        <div className="modal-body">
          <label>
            <span>{t('gp_teor_actual')} (g)</span>
            <input type="number" step="0.01" value={werkelijk} onChange={(e) => setWerkelijk(e.target.value)} placeholder="0.00" autoFocus />
          </label>
          <label>
            <span>{t('gp_teor_water')} (g)</span>
            <input type="number" step="0.01" value={water} onChange={(e) => setWater(e.target.value)} placeholder="0.00" />
          </label>
          <div className="gp-teor-result">
            <span className="muted small">TEOR</span>
            <span className="gp-teor-value">{teor !== null ? `${teor.toFixed(2)} %` : '—'}</span>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-primary" onClick={onClose}>{t('close')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DraftRow({ note, draft, defaultRate, saving, onField, onConfirm, onCancel, t }) {
  const grams = parseFloat(draft.grams) || 0;
  const price = parseFloat(draft.price_per_gram) || 0;
  const rate = parseFloat(draft.rate_srd) || defaultRate;
  const totalUsd = grams * price;
  const totalSrd = totalUsd * rate;

  const remUsd = totalUsd - (parseFloat(draft.paid_usd) || 0) - (rate ? (parseFloat(draft.paid_srd) || 0) / rate : 0);
  const rem = remUsd > 0.005 ? remUsd : 0;
  const phUsd = rem ? money(rem) : '—';
  const phSrd = rem ? money(rem * rate) : '—';
  // Live difference (SRD): owed - paid.
  const diff = totalSrd - (parseFloat(draft.paid_usd) || 0) * rate - (parseFloat(draft.paid_srd) || 0);
  const diffZero = Math.abs(diff) < 0.005 || (totalSrd === 0);

  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); onConfirm(); }
    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };

  return (
    <tr className="gp-draft-row" onKeyDown={onKey}>
      <td className="gp-note">{note}</td>
      <td className="gp-ro kassa-time muted">—</td>
      <td className="gp-num">
        <input className="gp-cell" type="number" step="0.01" inputMode="decimal" autoFocus
          value={editVal(draft.grams)} onChange={(e) => onField('grams', e.target.value)} placeholder="0.00" />
      </td>
      <td className="gp-num">
        <input className="gp-cell" type="number" step="0.01" inputMode="decimal"
          value={editVal(draft.price_per_gram)} onChange={(e) => onField('price_per_gram', e.target.value)} placeholder="0.00" />
      </td>
      <td className="gp-num gp-computed">$ {money(totalUsd)}</td>
      <td className="gp-num gp-computed">SRD {money(totalSrd)}</td>
      <td className="gp-num">
        <input className="gp-cell gp-rate-cell" type="number" step="0.001" inputMode="decimal"
          value={editVal(draft.rate_srd)} onChange={(e) => onField('rate_srd', e.target.value)} placeholder={String(defaultRate)} />
      </td>
      <td className="gp-num gp-paid-col">
        <input className="gp-cell" type="number" step="0.01" inputMode="decimal"
          value={editVal(draft.paid_usd)} onChange={(e) => onField('paid_usd', e.target.value)} placeholder={phUsd} />
      </td>
      <td className="gp-num gp-paid-col">
        <input className="gp-cell" type="number" step="0.01" inputMode="decimal"
          value={editVal(draft.paid_srd)} onChange={(e) => onField('paid_srd', e.target.value)} placeholder={phSrd} />
      </td>
      <td className="gp-num gp-paid-col">
        <input className="gp-cell" type="number" step="0.01" inputMode="decimal"
          value={editVal(draft.paid_eur)} onChange={(e) => onField('paid_eur', e.target.value)} placeholder="—" />
      </td>
      <td className={'gp-num ' + (diffZero ? 'gp-ro' : (diff > 0 ? 'gp-gold-pos' : 'gp-gold-neg'))}>
        {diffZero ? '—' : `${diff > 0 ? '+' : '−'} SRD ${money(Math.abs(diff))}`}
      </td>
      <td className="gp-actions-col">
        <div className="gp-row-actions">
          <button className="btn btn-primary btn-icon" title={t('gp_confirm')} onClick={onConfirm} disabled={saving}><FiCheck /></button>
          <button className="btn btn-ghost btn-icon" title={t('cancel')} onClick={onCancel} disabled={saving}><FiX /></button>
        </div>
      </td>
    </tr>
  );
}

function GoldAdjustModal({ current, onCancel, onApply, t }) {
  const [mode, setMode] = useState('subtract');
  const [amount, setAmount] = useState('');
  const submit = (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt)) return;
    onApply(mode === 'set' ? 'set' : 'subtract', amt);
  };
  const preview = (() => {
    const amt = parseFloat(amount) || 0;
    return mode === 'set' ? amt : current - amt;
  })();
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-header"><h3>{t('gp_adjust_gold')}</h3></div>
        <form className="modal-body" onSubmit={submit}>
          <div className="muted small">{t('gp_gold_closing')}: <strong>{grams3(current)} g</strong></div>
          <div className="segmented" role="tablist" style={{ marginTop: '4px' }}>
            <button type="button" className={'segmented-item' + (mode === 'subtract' ? ' active' : '')} onClick={() => setMode('subtract')}>{t('gp_gold_subtract')}</button>
            <button type="button" className={'segmented-item' + (mode === 'set' ? ' active' : '')} onClick={() => setMode('set')}>{t('gp_gold_reset')}</button>
          </div>
          <label>
            <span>{mode === 'set' ? t('gp_gold_new_total') : t('gp_gold_subtract_amount')} (g)</span>
            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" autoFocus />
          </label>
          <div className="muted small">→ {t('gp_gold_closing')}: <strong>{grams3(preview)} g</strong></div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onCancel}>{t('cancel')}</button>
            <button type="submit" className="btn btn-primary">{t('gp_apply')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
